import { z } from 'zod';

/**
 * Outreach: a tailored email to a business this research collected.
 *
 * The flow is deliberately draft → review → send. A draft is never delivered on its own, and
 * sending is always one explicit action on one message, because an email that goes out under
 * your name cannot be recalled. Every draft records the facts it was built from, so a reader can
 * check the claims in it before it reaches anyone.
 */
export const OUTREACH_TONES = ['professional', 'warm', 'direct', 'brief'] as const;
export type OutreachTone = (typeof OUTREACH_TONES)[number];

export const OUTREACH_TONE_LABELS: Record<OutreachTone, string> = {
  professional: 'Professional',
  warm: 'Warm',
  direct: 'Direct',
  brief: 'Very brief',
};

export const OUTREACH_STATUSES = ['draft', 'sending', 'sent', 'failed'] as const;
export type OutreachStatus = (typeof OUTREACH_STATUSES)[number];

export const DraftOutreachSchema = z.object({
  accountId: z.string().min(1).max(64),
  /** Address a specific person found at the business, rather than the company inbox. */
  personId: z.string().max(64).optional(),
  tone: z.enum(OUTREACH_TONES).default('professional'),
  /** Optional steer from the user, e.g. "lead with the fact they have no website". */
  angle: z.string().trim().max(300).optional(),
  /** Which of 2.0's services to pitch. Empty = let the draft choose from the collected facts. */
  services: z.array(z.string().trim().min(1).max(80)).max(5).default([]),
});
export type DraftOutreachRequest = z.input<typeof DraftOutreachSchema>;

export const UpdateOutreachSchema = z.object({
  subject: z.string().trim().min(1, 'The email needs a subject.').max(200).optional(),
  body: z.string().trim().min(1, 'The email needs a body.').max(20_000).optional(),
  toEmail: z.string().trim().max(254).optional(),
  toName: z.string().trim().max(120).optional(),
});

export const OutreachQuerySchema = z.object({
  projectId: z.string().max(64).optional(),
  accountId: z.string().max(64).optional(),
  status: z.enum(['all', ...OUTREACH_STATUSES]).default('all'),
  offset: z.number().int().min(0).max(100_000).default(0),
  limit: z.number().int().min(1).max(100).default(50),
});

/** Confirmation is required per message: the id is echoed back so a stray click cannot send. */
export const SendOutreachSchema = z.object({
  confirm: z.literal(true, { message: 'Confirm before sending.' }),
});

export interface OutreachDraftDto {
  id: string;
  accountId: string;
  accountName: string | null;
  personId: string | null;
  toEmail: string;
  toName: string | null;
  subject: string;
  /** Plain-text body the user edits. The branded HTML wrapper is applied at send time. */
  body: string;
  status: OutreachStatus;
  tone: OutreachTone;
  /** 'gemini' when a model drafted it, 'template' when written from the facts alone. */
  writer: 'gemini' | 'template';
  /** The facts the draft was built from — shown next to it so claims can be checked. */
  basis: string[];
  servicesPitched: string[];
  errorMessage: string | null;
  createdAt: number;
  updatedAt: number;
  sentAt: number | null;
}

export interface OutreachListResponse {
  items: OutreachDraftDto[];
  total: number;
  offset: number;
  limit: number;
}

/** Wrapping is applied at send time, so the editor stays plain text the user fully controls. */
export function bodyToParagraphs(body: string): string[] {
  return body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}
